import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar as CalendarIcon, Users, Mail, Phone, MapPin, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Calendar } from "@/components/ui/calendar";

const BookingCalendar = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedDayBookings, setSelectedDayBookings] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    fetchConfirmedBookings();
  }, []);

  useEffect(() => {
    filterBookingsBySelectedDate();
  }, [selectedDate, bookings, searchQuery]);

  const fetchConfirmedBookings = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/bookings/confirmed/calendar`, { headers });
      setBookings(response.data);
    } catch (error) {
      console.error("Error fetching confirmed bookings:", error);
    } finally {
      setLoading(false);
    }
  };

  const filterBookingsBySelectedDate = () => {
    if (!selectedDate) {
      setSelectedDayBookings([]);
      return;
    }

    const dateStr = selectedDate.toISOString().split('T')[0];
    let filtered = bookings.filter(booking => booking.event_date === dateStr);

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(booking =>
        booking.name.toLowerCase().includes(query) ||
        booking.email.toLowerCase().includes(query) ||
        booking.phone.toLowerCase().includes(query) ||
        booking.address.toLowerCase().includes(query) ||
        booking.event_type.toLowerCase().includes(query) ||
        (booking.invoice_number && booking.invoice_number.toLowerCase().includes(query))
      );
    }

    setSelectedDayBookings(filtered);
  };

  const getBookingDates = () => {
    return bookings.map(booking => new Date(booking.event_date));
  };

  const hasBookingOnDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return bookings.some(booking => booking.event_date === dateStr);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#8B7355] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading calendar...</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="booking-calendar">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">Booking Calendar</h1>

      {/* Search Bar */}
      {selectedDayBookings.length > 0 && (
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <Input
              type="text"
              placeholder="Search bookings for selected date..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="calendar-search-input"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Calendar View */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <CalendarIcon className="mr-2 text-[#8B7355]" size={24} />
              Confirmed Bookings
            </CardTitle>
          </CardHeader>
          <CardContent className="flex justify-center">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={setSelectedDate}
              className="rounded-md border"
              modifiers={{
                booked: getBookingDates(),
              }}
              modifiersStyles={{
                booked: {
                  backgroundColor: '#8B7355',
                  color: 'white',
                  fontWeight: 'bold',
                },
              }}
            />
          </CardContent>
        </Card>

        {/* Selected Date Details */}
        <div>
          <Card>
            <CardHeader>
              <CardTitle>
                {selectedDate ? formatDate(selectedDate.toISOString()) : "Select a Date"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {selectedDayBookings.length > 0 ? (
                <div className="space-y-4">
                  {selectedDayBookings.map((booking, index) => (
                    <Card key={booking.id} className="border-[#8B7355]" data-testid={`calendar-booking-${index}`}>
                      <CardContent className="pt-6">
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <h3 className="font-bold text-lg">{booking.name}</h3>
                            <Badge className="mt-2 bg-green-100 text-green-800">
                              Confirmed
                            </Badge>
                          </div>
                          {booking.invoice_number && (
                            <span className="text-sm text-gray-600">
                              {booking.invoice_number}
                            </span>
                          )}
                        </div>

                        <div className="space-y-2 text-sm">
                          <div className="flex items-center text-gray-700">
                            <Mail size={14} className="mr-2" />
                            {booking.email}
                          </div>
                          <div className="flex items-center text-gray-700">
                            <Phone size={14} className="mr-2" />
                            {booking.phone}
                          </div>
                          <div className="flex items-start text-gray-700">
                            <MapPin size={14} className="mr-2 mt-1 flex-shrink-0" />
                            <span>{booking.address}</span>
                          </div>
                          <div className="flex items-center text-gray-700">
                            <Users size={14} className="mr-2" />
                            {booking.guest_count} guests
                          </div>
                          <div className="text-gray-700">
                            <strong>Event Type:</strong> {booking.event_type}
                          </div>
                          {booking.message && (
                            <div className="text-gray-700 pt-2 border-t">
                              <strong>Notes:</strong> {booking.message}
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <CalendarIcon className="mx-auto mb-4 text-gray-400" size={48} />
                  <p className="text-gray-600">
                    {selectedDate
                      ? "No confirmed bookings on this date"
                      : "Select a date to view bookings"}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <h3 className="text-3xl font-bold text-[#8B7355]">{bookings.length}</h3>
              <p className="text-gray-600 mt-2">Total Confirmed Bookings</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <h3 className="text-3xl font-bold text-[#8B7355]">
                {new Set(bookings.map(b => b.event_date)).size}
              </h3>
              <p className="text-gray-600 mt-2">Unique Event Dates</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <h3 className="text-3xl font-bold text-[#8B7355]">
                {bookings.reduce((sum, b) => sum + b.guest_count, 0)}
              </h3>
              <p className="text-gray-600 mt-2">Total Guests</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BookingCalendar;
